import { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'
interface EditableContentProps {
  value: string
  onChange: (value: string) => void
  onBlur?: (value: string) => void
  placeholder?: string
  placeholderColor?: string
  isEditing?: boolean
  maxHeight?: string
  className?: string
  singleLine?: boolean
  onEditingChange?: (editing: boolean) => void
  disabled?: boolean
  maxLines?: number
  autoSelectContent?: boolean
}

export const EditableContent = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Click to edit',
  placeholderColor = 'rgba(255, 255, 255, 0.45)',
  maxHeight = '300px',
  className,
  singleLine = false,
  onEditingChange,
  disabled = false,
  maxLines,
  autoSelectContent = false
}: EditableContentProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [initialValue, setInitialValue] = useState(value)
  const contentRef = useRef<HTMLDivElement>(null)
  const isUserEditing = useRef(false)
  const previousValueRef = useRef(value)

  const normalizeToPlainText = (input: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = input
    tmp.querySelectorAll('br').forEach(br => (br.outerHTML = '\n'))
    tmp.querySelectorAll('div,p,li,ul,ol,pre').forEach(el => {
      el.insertAdjacentText('beforebegin', '\n')
      el.insertAdjacentText('afterend', '\n')
    })
    let text = tmp.textContent || ''
    text = text.replace(/\r\n?/g, '\n')
    text = text.replace(/\n{3,}/g, '\n\n')
    if (singleLine) {
      text = text.replace(/\n/g, '')
    }
    return text
  }

  const setElText = (el: HTMLDivElement, text: string) => {
    const normalized = singleLine ? text.replace(/\n/g, '') : text
    if (el.textContent !== normalized) el.textContent = normalized
  }

  const placeCaretEnd = (el: HTMLElement) => {
    const range = document.createRange()
    const sel = window.getSelection()
    if (el.childNodes.length > 0) {
      const lastChild = el.childNodes[el.childNodes.length - 1]
      range.setStart(lastChild, lastChild.textContent?.length || 0)
    } else {
      range.setStart(el, 0)
    }
    range.collapse(true)
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  const handleClick = () => {
    if (!isEditing && !disabled) {
      setIsEditing(true)
      setInitialValue(value)
      onEditingChange?.(true)
      isUserEditing.current = false
    }
  }

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const text = singleLine ? value.replace(/\n/g, '') : value

    if (!isEditing || !isUserEditing.current) {
      setElText(el, text)
    }

    previousValueRef.current = value

    if (isEditing && !isUserEditing.current) {
      if (autoSelectContent) {
        const t = el.innerText
        const lastDotIndex = t.lastIndexOf('.')
        const isValidDot = lastDotIndex > 0 && lastDotIndex < t.length - 1
        if (isValidDot && el.firstChild) {
          const range = document.createRange()
          const sel = window.getSelection()
          range.setStart(el.firstChild, 0)
          range.setEnd(el.firstChild, lastDotIndex)
          sel?.removeAllRanges()
          sel?.addRange(range)
          el.focus()
          return
        }
      }
      placeCaretEnd(el)
      el.focus()
    }
  }, [isEditing, value, singleLine, autoSelectContent])

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const onBeforeInput = (e: InputEvent) => {
      const type = (e as any).inputType || ''
      if (
        type.startsWith('format') ||
        type === 'insertFromPaste' ||
        type === 'insertFromPasteAsQuotation' ||
        type === 'insertFromDrop'
      ) {
        e.preventDefault()
        if (type.startsWith('insertFromPaste')) {
          const clipboard = (e as ClipboardEvent).clipboardData
          const text = normalizeToPlainText(clipboard?.getData('text') || '')
          document.execCommand('insertText', false, text)
        }
      }
    }

    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      const text = normalizeToPlainText(e.dataTransfer?.getData('text/plain') || '')
      document.execCommand('insertText', false, text)
    }

    el.addEventListener('beforeinput', onBeforeInput as any)
    el.addEventListener('drop', onDrop)

    return () => {
      el.removeEventListener('beforeinput', onBeforeInput as any)
      el.removeEventListener('drop', onDrop)
    }
  }, [singleLine])

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    isUserEditing.current = true
    const plain = normalizeToPlainText(e.currentTarget.innerHTML)
    if (e.currentTarget.textContent !== plain) {
      e.currentTarget.textContent = plain
      placeCaretEnd(e.currentTarget)
    }
    onChange(plain)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const text = normalizeToPlainText(e.clipboardData.getData('text'))
    document.execCommand('insertText', false, text)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const meta = e.ctrlKey || e.metaKey
    if (meta && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
      e.preventDefault()
      return
    }

    if (e.key === 'Escape' && isEditing) {
      e.preventDefault()
      setIsEditing(false)
      onEditingChange?.(false)
      onChange(initialValue)
      if (contentRef.current) {
        contentRef.current.textContent = initialValue
        contentRef.current.blur()
      }
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      setIsEditing(false)
      onEditingChange?.(false)
      onBlur?.(contentRef.current?.textContent || '')
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const plain = normalizeToPlainText(e.currentTarget.innerHTML)
    setIsEditing(false)
    isUserEditing.current = false
    onEditingChange?.(false)
    onBlur?.(plain)
    const sel = window.getSelection()
    sel?.removeAllRanges()
  }

  const handleMouseUp = () => {
    isUserEditing.current = true
  }

  return (
    <SEditableDiv
      ref={contentRef}
      className={className}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onInput={handleInput}
      onPaste={handlePaste}
      onBlur={handleBlur}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseUp={handleMouseUp}
      data-placeholder={placeholder}
      $placeholderColor={placeholderColor}
      $maxLines={maxLines}
      $isEditing={isEditing}
      style={{
        maxHeight,
        whiteSpace: singleLine ? 'nowrap' : 'pre-line'
      }}
    />
  )
}

interface SEditableDivProps {
  $placeholderColor: string
  $maxLines?: number
  $isEditing: boolean
}

const SEditableDiv = styled.div<SEditableDivProps>`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.88);
  cursor: text;
  text-wrap: balance;
  min-height: 22px;
  overflow-y: auto;
  padding: 8px 11px;
  border: 1px solid transparent;
  border-radius: 6px;
  transition: border-color 0.3s, box-shadow 0.3s;
  white-space: pre-line;
  outline: none;
  word-break: break-word;
  overflow-wrap: break-word;

  ${props => props.$maxLines && !props.$isEditing && `
    display: -webkit-box;
    -webkit-line-clamp: ${props.$maxLines};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-line;
  `}

  ${props => props.$isEditing && `
    display: block;
    overflow-y: auto;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
    text-overflow: unset;
  `}

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(135, 71, 223, 0.10)
  }

  &:empty:before {
    content: attr(data-placeholder);
    color: ${props => props.$placeholderColor};
  }

&::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
    padding: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.25);
  }
`